/**
 * CDM Non-Human Identities — service accounts, workload identities,
 * GitHub Apps, k8s ServiceAccounts.
 *
 * Per docs/cdm-design-v4.md §14: orphan candidates flag accounts with
 * no person link. Phase 11 ships the agentic owner-inference loop;
 * this page is the inventory + 'orphan candidate' surface that backs it.
 */
import { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import {
  Bot, RefreshCw, ChevronLeft, Search, Filter, AlertTriangle, Shield, Brain,
} from 'lucide-react'
import { clsx } from 'clsx'
import { api } from '../lib/api'

interface NHI {
  user_id: string
  kind: string
  state: string
  upn: string | null
  person_id: string | null
  sources: string[]
  github_app_slug: string | null
  github_org_role: string | null
  k8s_cluster: string | null
  k8s_namespace: string | null
  k8s_owner_kind: string | null
  k8s_owner_name: string | null
  github_app_has_admin: boolean | null
  is_orphan_candidate: boolean
}

const KIND_TONE: Record<string, string> = {
  service_account:    'bg-purple/10 text-purple-light border-purple/30',
  workload_identity:  'bg-cyan/10 text-cyan border-cyan/30',
  system_account:     'bg-text-muted/10 text-text-muted border-border',
}

export default function CdmNHIsPage() {
  const [nhis, setNhis] = useState<NHI[]>([])
  const [loading, setLoading] = useState(false)
  const [search, setSearch] = useState('')
  const [orphanOnly, setOrphanOnly] = useState(false)
  const [adminOnly, setAdminOnly] = useState(false)
  const [running, setRunning] = useState(false)
  const [message, setMessage] = useState('')

  const load = () => {
    setLoading(true)
    api.get('/api/cdm/nhis', { params: { limit: 500 } })
      .then(r => setNhis(r.data?.nhis || []))
      .catch(() => {})
      .finally(() => setLoading(false))
  }
  useEffect(() => { load() }, [])

  const runClassifier = async () => {
    setRunning(true)
    setMessage('Running orphan classifier (Planner → Critic)…')
    try {
      const { data } = await api.post('/api/cdm/policy-proposals/agent/orphans/run')
      setMessage(`Classifier: ${data.candidates_scanned} candidates · ${data.inferences_made} inferences · ${data.proposals_inserted} proposals queued for review`)
      load()
    } catch (e: any) {
      setMessage(`Classifier: ${e.response?.data?.detail || 'failed'}`)
    } finally {
      setRunning(false)
    }
  }

  const filtered = useMemo(() =>
    nhis.filter(n => !search ||
      n.upn?.toLowerCase().includes(search.toLowerCase()) ||
      n.github_app_slug?.toLowerCase().includes(search.toLowerCase()) ||
      n.k8s_namespace?.toLowerCase().includes(search.toLowerCase()) ||
      n.k8s_owner_name?.toLowerCase().includes(search.toLowerCase()))
      .filter(n => !orphanOnly || n.is_orphan_candidate)
      .filter(n => !adminOnly || n.github_app_has_admin === true),
  [nhis, search, orphanOnly, adminOnly])

  const orphanCount = nhis.filter(n => n.is_orphan_candidate).length
  const adminCount = nhis.filter(n => n.github_app_has_admin === true).length

  return (
    <div className="space-y-4 animate-fade-in">
      <div className="flex items-baseline gap-4 pb-2 border-b border-border flex-wrap">
        <Link to="/app/cdm" className="text-text-muted hover:text-text-primary inline-flex items-center gap-1">
          <ChevronLeft size={14} /> CDM
        </Link>
        <h1 className="text-lg font-bold text-text-primary flex items-center gap-2">
          <Bot size={18} className="text-purple-light" /> NON-HUMAN IDENTITIES
        </h1>
        <span className="text-[11px] uppercase tracking-widest text-text-muted">
          service accounts · workload identities · GitHub apps · k8s SAs
        </span>
        <div className="ml-auto flex items-center gap-2">
          <Link to="/app/cdm/policy-proposals?target=orphan_owner"
                className="btn-ghost text-[11px]">
            View proposals →
          </Link>
          <button type="button" onClick={runClassifier} disabled={running} className="btn-ghost"
                  title="Run orphan classifier (admin only)">
            <Brain size={11} /> Run classifier
          </button>
          <button type="button" onClick={load} disabled={loading} className="btn-ghost">
            <RefreshCw size={11} className={loading ? 'animate-spin' : ''} /> Refresh
          </button>
        </div>
      </div>

      {message && (
        <div className="text-[11px] text-text-secondary font-mono px-3 py-2 border border-border rounded bg-surface-card">
          {message}
        </div>
      )}

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Tile label="Total NHIs" value={nhis.length} tone="text-purple-light" />
        <Tile label="Orphan candidates" value={orphanCount} tone="text-amber-light" warn={orphanCount > 0} />
        <Tile label="Admin-grade" value={adminCount} tone="text-rose-light" warn={adminCount > 0} />
        <Tile label="K8s SAs" value={nhis.filter(n => n.k8s_cluster).length} tone="text-cyan" />
      </div>

      <div className="flex items-center gap-2 text-xs flex-wrap">
        <Filter size={12} className="text-text-muted" />
        <button type="button" onClick={() => setOrphanOnly(!orphanOnly)}
          className={clsx('px-2 py-1 rounded border',
            orphanOnly ? 'bg-amber/10 text-amber-light border-amber/30' : 'border-border text-text-secondary')}>
          Orphans only
        </button>
        <button type="button" onClick={() => setAdminOnly(!adminOnly)}
          className={clsx('px-2 py-1 rounded border',
            adminOnly ? 'bg-rose/10 text-rose-light border-rose/30' : 'border-border text-text-secondary')}>
          Admin-grade only
        </button>
        <div className="relative flex-1 max-w-sm">
          <Search size={12} className="absolute left-2 top-1/2 -translate-y-1/2 text-text-muted" />
          <input type="search" value={search} onChange={e => setSearch(e.target.value)}
            placeholder="search upn / app slug / namespace / owner"
            className="input pl-7 w-full" />
        </div>
        <span className="text-text-muted ml-auto">{filtered.length} of {nhis.length}</span>
      </div>

      <div className="card p-0 overflow-hidden">
        <table className="w-full text-xs">
          <thead className="bg-surface-raised">
            <tr className="text-[10px] uppercase tracking-widest text-text-muted border-b border-border">
              <th className="text-left px-3 py-2">Identifier</th>
              <th className="text-left px-3 py-2">Kind</th>
              <th className="text-left px-3 py-2">Origin</th>
              <th className="text-left px-3 py-2">Owner / Workload</th>
              <th className="text-left px-3 py-2">Flags</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {filtered.length === 0 && (
              <tr><td colSpan={5} className="px-3 py-8 text-center text-text-muted">
                No NHIs in CDM yet. Configure GitHub / Kubernetes / Entra adapters
                to populate this view.
              </td></tr>
            )}
            {filtered.map(n => (
              <tr key={n.user_id} className="hover:bg-surface-raised/40">
                <td className="px-3 py-1.5">
                  <Link to={`/app/cdm/users/${n.user_id}`}
                        className="text-blue-light hover:underline font-mono text-[11px]">
                    {n.upn || n.github_app_slug || n.k8s_owner_name || n.user_id.slice(0, 8) + '…'}
                  </Link>
                </td>
                <td className="px-3 py-1.5">
                  <span className={clsx('badge text-[9px] border', KIND_TONE[n.kind] || 'bg-surface-raised text-text-muted border-border')}>
                    {n.kind}
                  </span>
                </td>
                <td className="px-3 py-1.5 text-text-secondary text-[11px]">
                  {n.k8s_cluster ? `k8s:${n.k8s_cluster}/${n.k8s_namespace}` :
                   n.github_app_slug ? `github:${n.github_app_slug}` :
                   n.sources.join(', ')}
                </td>
                <td className="px-3 py-1.5 text-text-secondary text-[11px]">
                  {n.k8s_owner_kind && n.k8s_owner_name ? `${n.k8s_owner_kind}/${n.k8s_owner_name}` : '—'}
                </td>
                <td className="px-3 py-1.5">
                  {n.is_orphan_candidate && (
                    <span className="badge text-[9px] mr-1 bg-amber/10 text-amber-light border border-amber/30 inline-flex items-center gap-1">
                      <AlertTriangle size={9} /> orphan
                    </span>
                  )}
                  {n.github_app_has_admin && (
                    <span className="badge text-[9px] mr-1 bg-rose/10 text-rose-light border border-rose/30 inline-flex items-center gap-1">
                      <Shield size={9} /> admin
                    </span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <p className="text-[10px] text-text-muted text-center">
        Phase 11 will add the agentic owner-inference loop (Planner → Critic) on the orphan-candidate set,
        with a reverse-attestation campaign for human team-leads to claim or dispute.
      </p>
    </div>
  )
}


function Tile({ label, value, tone, warn }: { label: string; value: number; tone: string; warn?: boolean }) {
  return (
    <div className={clsx('card p-3', warn && 'ring-1 ring-amber/40')}>
      <div className="text-[10px] uppercase tracking-widest text-text-muted">{label}</div>
      <div className={clsx('text-2xl font-black font-mono mt-1', tone)}>{value.toLocaleString()}</div>
    </div>
  )
}
